import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check if user is already logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      if (api.isAuthenticated()) {
        try {
          const response = await api.verifyToken();
          setUser(response.data.user);
          setOrganization(response.data.organization);
          setPermissions(response.data.user?.permissions || []);
          setIsAuthenticated(true);

          // Store organization_id and user_id in localStorage for components that need them
          if (response.data.organization?.id) {
            localStorage.setItem('organization_id', response.data.organization.id);
          }
          if (response.data.user?.id) {
            localStorage.setItem('user_id', response.data.user.id);
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          api.logout();
          setIsAuthenticated(false);
          setUser(null);
          setOrganization(null);
          setPermissions([]);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.login({ email, password });
      const { user: userData, organization: orgData } = response.data;

      setUser(userData);
      setOrganization(orgData);
      setPermissions(userData?.permissions || []);
      setIsAuthenticated(true);

      // Store organization_id and user_id in localStorage
      if (orgData?.id) {
        localStorage.setItem('organization_id', orgData.id);
      }
      if (userData?.id) {
        localStorage.setItem('user_id', userData.id);
      }

      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Login failed. Please try again.';
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.register(userData);

      // For magic link registration, don't authenticate the user immediately
      // User will be authenticated after clicking the magic link in their email
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Registration failed. Please try again.';
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const setAuthenticatedUser = (userData, organizationData) => {
    setUser(userData);
    setOrganization(organizationData);
    setPermissions(userData?.permissions || []);
    setIsAuthenticated(true);

    // Store organization_id and user_id in localStorage
    if (organizationData?.id) {
      localStorage.setItem('organization_id', organizationData.id);
    }
    if (userData?.id) {
      localStorage.setItem('user_id', userData.id);
    }
  };

  const logout = () => {
    api.logout();
    setUser(null);
    setOrganization(null);
    setPermissions([]);
    setIsAuthenticated(false);

    // Clear organization_id and user_id from localStorage
    localStorage.removeItem('organization_id');
    localStorage.removeItem('user_id');

    navigate('/login');
  };

  // Helper function to check if user has a specific permission
  const hasPermission = (permissionName) => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.some(perm => perm.name === permissionName);
  };

  // Helper function to check if user has any of the specified permissions
  const hasAnyPermission = (permissionNames) => {
    if (!permissions || permissions.length === 0) return false;
    return permissionNames.some(permName => hasPermission(permName));
  };

  const value = {
    isAuthenticated,
    user,
    organization,
    permissions,
    loading,
    login,
    register,
    logout,
    setAuthenticatedUser,
    hasPermission,
    hasAnyPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
